import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle, AlertCircle, Download, Facebook, Instagram, Search, BookOpen, ChevronDown, ChevronUp, ExternalLink, Radio, Tv, Mail, Globe, Newspaper, Megaphone, Link2, Pencil, MapPin, RotateCcw, X } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import MetaConnection from "./MetaConnection";
import ContentEffectImporter from "./ContentEffectImporter";
import LiveChatImporter from "./LiveChatImporter";
import VisitorIdImporter from "./VisitorIdImporter";
import PmaxImporter from "./PmaxImporter";
import CampaignSummaryImporter from "./CampaignSummaryImporter";
import { DateMismatchDialog } from "./DateMismatchDialog";
import { Sparkles as PmaxIcon, Layers } from "lucide-react";

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function ImportData({ clients, onImport, API }) {
  const [selectedClient, setSelectedClient] = useState("");
  const [importType, setImportType] = useState("facebook");
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showBestPractices, setShowBestPractices] = useState(false);
  const [marketingChannels, setMarketingChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [importStartDate, setImportStartDate] = useState("");
  const [importEndDate, setImportEndDate] = useState("");
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [brandChannels, setBrandChannels] = useState([]);
  const [newBrandChannel, setNewBrandChannel] = useState({ name: "", impressions: "", local_spots: "", report_start: "", report_end: "", description: "", notes: "" });
  const [zipcodeInput, setZipcodeInput] = useState("");
  const [savingZipcodes, setSavingZipcodes] = useState(false);
  const [contentEffectOpen, setContentEffectOpen] = useState(false);
  const [liveChatOpen, setLiveChatOpen] = useState(false);
  const [visitorIdOpen, setVisitorIdOpen] = useState(false);
  const [pmaxOpen, setPmaxOpen] = useState(false);
  const [csOpen, setCsOpen] = useState(false);
  const [dateCheck, setDateCheck] = useState(null);
  const [smExisting, setSmExisting] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState("");
  const uploadAreaRef = useRef(null);

  // Get current client info for multi-location support
  const currentClient = clients.find(c => c.id === selectedClient);
  const isMultiLocation = currentClient?.client_type === 'multi';
  const clientLocations = currentClient?.locations || [];

  // Auto-fill import dates from client's report period when client changes
  useEffect(() => {
    if (selectedClient) {
      const cl = clients.find(c => c.id === selectedClient);
      if (cl) {
        setImportStartDate(cl.report_start || "");
        setImportEndDate(cl.report_end || "");
      }
      // Fetch upload history
      setLoadingHistory(true);
      axios.get(`${API}/upload-history?client_id=${selectedClient}`)
        .then(res => setUploadHistory(res.data))
        .catch(() => setUploadHistory([]))
        .finally(() => setLoadingHistory(false));
    } else {
      setImportStartDate("");
      setImportEndDate("");
      setUploadHistory([]);
    }
  }, [selectedClient, clients, API]);

  // Warn before a second report for the same client + period
  const [dupExisting, setDupExisting] = useState(null);
  useEffect(() => {
    setSmExisting(null);
    setDupExisting(null);
    if (!selectedClient || !importStartDate || !importEndDate) return;
    const loc = selectedLocation ? `&location_name=${encodeURIComponent(selectedLocation)}` : "";
    const range = `client_id=${selectedClient}&report_start=${importStartDate}&report_end=${importEndDate}${loc}`;
    if (importType === "social-mirror") {
      axios.get(`${API}/social-mirror-existing?${range}`).then((res) => setSmExisting(res.data)).catch(() => {});
      return;
    }
    const kind = { facebook: "facebook", "mobile-conquesting": "mobile", "campaign-dashboard": "ctv" }[importType];
    if (!kind) return;
    axios.get(`${API}/upload-existing?kind=${kind}&${range}`).then((res) => setDupExisting(res.data)).catch(() => {});
  }, [importType, selectedClient, importStartDate, importEndDate, selectedLocation, uploadHistory, API]);

  // Available marketing channels configuration
  const availableChannels = [
    // Paid Social
    { id: 'facebook', name: 'Paid Facebook Ads', icon: '📘', category: 'Paid Social' },
    { id: 'instagram', name: 'Paid Instagram Ads', icon: '📸', category: 'Paid Social' },
    { id: 'tiktok', name: 'TikTok', icon: '🎵', category: 'Paid Social' },
    { id: 'google', name: 'Google Ads', icon: '🔍', category: 'Paid Social' },
    // Display
    { id: 'social_mirror', name: 'Social Mirror Ads', icon: '🪞', category: 'Display' },
    // Video
    { id: 'ctv', name: 'CTV (Connected TV)', icon: '📺', category: 'Video' },
    { id: 'local_tv', name: 'Local TV', icon: '📡', category: 'Video' },
    // Audio
    { id: 'radio', name: 'Internet Radio Ads', icon: '📻', category: 'Audio' },
    { id: 'local_radio', name: 'Local Radio', icon: '🎙️', category: 'Audio' },
    { id: 'podcast', name: 'Podcast', icon: '🎧', category: 'Audio' },
    // Social
    { id: 'organic_social', name: 'Organic Social Posts', icon: '🌐', category: 'Social' },
    // Direct
    { id: 'email', name: 'Email', icon: '✉️', category: 'Direct' },
    { id: 'mailings', name: 'Direct Mail', icon: '📬', category: 'Direct' },
    // Loyalty
    { id: 'loyalty_app', name: 'Loyalty App Programs', icon: '⭐', category: 'Loyalty' },
    // Traditional
    { id: 'billboard', name: 'Billboard', icon: '🪧', category: 'Traditional' },
    { id: 'print', name: 'Print', icon: '📰', category: 'Traditional' },
  ];

  // Fetch marketing channels when client changes
  useEffect(() => {
    if (selectedClient && importType === 'channels') {
      fetchMarketingChannels();
      // Load brand channels and target zipcodes from client data
      const cl = clients.find(c => c.id === selectedClient);
      setBrandChannels(cl?.brand_channels || []);
      setZipcodeInput((cl?.target_zipcodes || []).join(', '));
    }
  }, [selectedClient, importType, clients]);

  const fetchMarketingChannels = async () => {
    if (!selectedClient) return;
    setLoadingChannels(true);
    try {
      const response = await axios.get(`${API}/marketing-channels?client_id=${selectedClient}&active_only=false`);
      setMarketingChannels(response.data || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
      setMarketingChannels([]);
    } finally {
      setLoadingChannels(false);
    }
  };

  const saveBrandChannels = async (channels) => {
    try {
      await axios.put(`${API}/clients/${selectedClient}`, {
        brand_channels: channels,
      });
      setBrandChannels(channels);
      toast.success("Brand channels updated");
      if (onImport) onImport();
    } catch (error) {
      toast.error("Failed to save brand channels");
    }
  };

  const saveTargetZipcodes = async () => {
    if (!selectedClient) return;
    setSavingZipcodes(true);
    try {
      const zips = zipcodeInput.split(/[,\s\n]+/).map(z => z.trim()).filter(z => /^\d{5}$/.test(z));
      await axios.put(`${API}/clients/${selectedClient}`, {
        target_zipcodes: zips,
      });
      setZipcodeInput(zips.join(', '));
      toast.success(`${zips.length} target zip codes saved`);
      if (onImport) onImport();
    } catch (error) {
      toast.error("Failed to save zip codes");
    }
    setSavingZipcodes(false);
  };


  const addBrandChannel = () => {
    if (!newBrandChannel.name.trim()) return;
    const channelData = { 
      name: newBrandChannel.name.trim(), 
      status: "Active",
      impressions: newBrandChannel.impressions || "",
      local_spots: newBrandChannel.local_spots || "",
      report_start: newBrandChannel.report_start || "",
      report_end: newBrandChannel.report_end || "",
      description: newBrandChannel.description || "",
      notes: newBrandChannel.notes || "",
    };
    let updated;
    if (newBrandChannel.editIdx !== undefined) {
      updated = [...brandChannels];
      updated[newBrandChannel.editIdx] = channelData;
    } else {
      updated = [...brandChannels, channelData];
    }
    saveBrandChannels(updated);
    setNewBrandChannel({ name: "", impressions: "", local_spots: "", report_start: "", report_end: "", description: "", notes: "" });
  };

  const removeBrandChannel = async (idx) => {
    const channelName = brandChannels[idx]?.name || "this channel";
    if (!window.confirm(`Remove "${channelName}" from brand channels?`)) return;
    const updated = brandChannels.filter((_, i) => i !== idx);
    try {
      await axios.put(`${API}/clients/${selectedClient}`, {
        brand_channels: updated,
      });
      setBrandChannels(updated);
      toast.success(`Removed "${channelName}"`);
      if (onImport) onImport();
    } catch (error) {
      toast.error(`Failed to remove "${channelName}"`);
    }
  };

  const toggleChannel = async (channelId) => {
    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    
    const existingChannel = marketingChannels.find(c => c.channel_type === channelId);
    
    try {
      if (existingChannel) {
        // Toggle existing channel
        await axios.put(`${API}/marketing-channels/${existingChannel.id}/toggle`);
        toast.success(`${availableChannels.find(c => c.id === channelId)?.name} ${existingChannel.is_active ? 'disabled' : 'enabled'}`);
      } else {
        // Add new channel
        await axios.post(`${API}/marketing-channels`, {
          client_id: selectedClient,
          channel_type: channelId,
          is_active: true
        });
        toast.success(`${availableChannels.find(c => c.id === channelId)?.name} enabled`);
      }
      // Refresh channels
      fetchMarketingChannels();
    } catch (error) {
      console.error('Error toggling channel:', error);
      toast.error('Failed to update channel');
    }
  };

  const isChannelActive = (channelId) => {
    const channel = marketingChannels.find(c => c.channel_type === channelId);
    return channel?.is_active || false;
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    const validExtensions = [".csv", ".xlsx", ".xls", ".pdf", ".zip"];
    const isValid = validExtensions.some(ext => droppedFile?.name.toLowerCase().endsWith(ext));
    if (droppedFile && isValid) {
      setFile(droppedFile);
      setUploadResult(null);
    } else {
      toast.error("Please upload a CSV, Excel, PDF, or ZIP file");
    }
  }, []);

  const refreshHistory = () => {
    if (selectedClient) {
      axios.get(`${API}/upload-history?client_id=${selectedClient}`)
        .then(res => setUploadHistory(res.data))
        .catch(() => {});
    }
  };

  const handleDeleteUpload = async (uploadId, filename) => {
    if (!window.confirm(`Delete "${filename}" and all its imported data?`)) return;
    try {
      await axios.delete(`${API}/upload-history/${uploadId}`);
      toast.success(`Deleted ${filename}`);
      refreshHistory();
      if (onImport) onImport();
    } catch (e) {
      toast.error("Failed to delete upload");
    }
  };

  const importTypeFromUpload = (uploadImportType) => {
    const map = {
      "Metrics CSV": "facebook",
      "Ad Metrics CSV": "ads",
      "Facebook/Meta CSV": "facebook",
      "Facebook/Meta PDF": "facebook",
      "Mobile Conquesting PDF": "mobile-conquesting",
      "Google Ads Campaigns": "google",
      "Google Ads CSV": "google",
      "Google Ads Search Terms": "google",
      "Google Analytics CSV": "analytics",
      "CTV / Programmatic": "campaign-dashboard",
      "CTV / Programmatic ZIP": "campaign-dashboard",
      "CTV Publishers": "campaign-dashboard",
      "CTV Dashboard PDF": "campaign-dashboard",
      "App Front Campaigns": "appfront",
      "Social Mirror / Display PDF": "social-mirror",
      "TikTok Ads": "tiktok",
    };
    return map[uploadImportType] || "facebook";
  };

  const handleReimport = (upload) => {
    const tab = importTypeFromUpload(upload.import_type);
    setImportType(tab);
    if (upload.report_start) setImportStartDate(upload.report_start);
    if (upload.report_end) setImportEndDate(upload.report_end);
    setFile(null);
    setUploadResult(null);
    toast.info(`Ready to re-import "${upload.filename}" — select the file below`, { duration: 4000 });
    setTimeout(() => {
      uploadAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
  };

  const handleWipeAllData = async () => {
    if (!window.confirm("This will permanently delete ALL imported data for this client. Are you sure?")) return;
    if (!window.confirm("This cannot be undone. Confirm wipe?")) return;
    try {
      const res = await axios.delete(`${API}/clients/${selectedClient}/data`);
      toast.success(res.data.message);
      refreshHistory();
      if (onImport) onImport();
    } catch (e) {
      toast.error("Failed to wipe data");
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    const validExtensions = [".csv", ".xlsx", ".xls", ".pdf", ".zip"];
    const isValid = validExtensions.some(ext => selectedFile?.name.toLowerCase().endsWith(ext));
    if (selectedFile && isValid) {
      setFile(selectedFile);
      setUploadResult(null);
    } else {
      toast.error("Please upload a CSV, Excel, PDF, or ZIP file");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    if (!selectedClient) {
      toast.error("Please select a client");
      return;
    }

    setIsUploading(true);
    try {
      let rs = importStartDate, re = importEndDate;
      if (file.name.toLowerCase().endsWith(".pdf")) {
        try {
          const pfd = new FormData();
          pfd.append("file", file);
          const peek = (await axios.post(`${API}/import/peek-dates`, pfd, { headers: { "Content-Type": "multipart/form-data" } })).data;
          if (peek?.date_start && peek?.date_end && rs && re && (peek.date_start !== rs || peek.date_end !== re)) {
            const choice = await new Promise((resolve) => setDateCheck({
              report: { start: peek.date_start, end: peek.date_end }, picked: { start: rs, end: re },
              resolve: (v) => { setDateCheck(null); resolve(v); },
            }));
            if (!choice) { setIsUploading(false); return; }
            rs = choice.start; re = choice.end;
            setImportStartDate(rs); setImportEndDate(re);
          }
        } catch (peekErr) {
          console.warn("Date check skipped:", peekErr);
        }
      }
      const formData = new FormData();
      formData.append("file", file);
      if (rs) formData.append("report_start", rs);
      if (re) formData.append("report_end", re);

      // Use platform-specific endpoint
      let endpoint = "import/metrics";
      const isPDF = file.name.toLowerCase().endsWith('.pdf');
      const isZip = file.name.toLowerCase().endsWith('.zip');
      
      if (importType === "facebook") {
        endpoint = isPDF ? "import/facebook-pdf" : "import/facebook";
      } else if (importType === "instagram") {
        endpoint = isPDF ? "import/facebook-pdf" : "import/facebook"; // Instagram uses same Meta format
      } else if (importType === "tiktok") {
        // Auto-detect demographics CSV: TikTok audience reports have "Audience Report" or "Age" in filename
        const lowerName = file.name.toLowerCase();
        if (lowerName.includes('audience') || lowerName.includes('age')) {
          endpoint = "import/tiktok-demographics";
        } else {
          endpoint = "import/tiktok";
        }
      } else if (importType === "google") {
        endpoint = "import/google";
      } else if (importType === "analytics") {
        endpoint = "import/analytics";
      } else if (importType === "campaign-dashboard") {
        if (isZip) {
          endpoint = "import/ctv-zip";
        } else if (isPDF) {
          endpoint = "import/ctv-dashboard-pdf";
        } else if (file.name.toLowerCase().includes('publisher')) {
          endpoint = "import/ctv-publishers";
        } else {
          endpoint = "import/campaign-dashboard";
        }
      } else if (importType === "social-mirror") {
        endpoint = "import/social-mirror";
      } else if (importType === "mobile-conquesting") {
        endpoint = "import/mobile-conquesting-pdf";
      } else if (importType === "ads") {
        endpoint = "import/ad-metrics";
      } else if (importType === "appfront") {
        endpoint = "import/appfront";
      }

      // Build query string with dates
      let queryStr = `client_id=${selectedClient}`;
      if (rs) queryStr += `&report_start=${encodeURIComponent(rs)}`;
      if (re) queryStr += `&report_end=${encodeURIComponent(re)}`;
      if (importType === "social-mirror" && selectedLocation) queryStr += `&location_name=${encodeURIComponent(selectedLocation)}`;

      const response = await axios.post(
        `${API}/${endpoint}?${queryStr}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Format result message
      let resultMessage = response.data.message;
      if (response.data.ads_imported !== undefined) {
        resultMessage = `Imported ${response.data.ads_imported} ads from ${response.data.campaigns_imported} campaigns`;
        if (response.data.rows_skipped > 0) {
          resultMessage += ` (${response.data.rows_skipped} rows skipped)`;
        }
      }

      setUploadResult({
        success: true,
        message: resultMessage,
        details: response.data,
      });
      
      // Tag the uploaded batch with location if multi-location client
      if (selectedLocation) {
        try {
          // Get the most recent upload to find the batch_id
          const histRes = await axios.get(`${API}/upload-history?client_id=${selectedClient}`);
          const latestUpload = histRes.data?.[0];
          if (latestUpload?.id) {
            await axios.post(`${API}/tag-location?batch_id=${latestUpload.id}&location_name=${encodeURIComponent(selectedLocation)}`);
          }
        } catch (tagErr) {
          console.error("Error tagging location:", tagErr);
        }
      }
      
      toast.success("Import successful!");
      onImport();
      refreshHistory();
      setFile(null);
    } catch (e) {
      console.error("Error uploading file:", e);
      const errorDetail = e.response?.data?.detail || e.response?.data?.message || e.message || "Failed to import data";
      setUploadResult({
        success: false,
        message: typeof errorDetail === 'string' ? errorDetail : "Failed to import data. Please try again.",
      });
      toast.error(typeof errorDetail === 'string' ? errorDetail : "Failed to import data");
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = (type) => {
    let csvContent = "";
    if (type === "metrics") {
      csvContent =
        "date,platform,impressions,clicks,ctr,spend,likes,comments,shares,new_followers\n" +
        "2024-01-15,facebook,10000,250,2.5,150.00,500,50,25,100\n" +
        "2024-01-15,instagram,8000,200,2.5,120.00,800,75,40,150";
    } else {
      csvContent =
        "date,platform,ad_name,creative_type,impressions,clicks,ctr,spend,conversions\n" +
        "2024-01-15,facebook,Summer Sale Banner,image,5000,125,2.5,75.00,15\n" +
        "2024-01-15,instagram,Product Video,video,8000,240,3.0,100.00,25";
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${type}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="import-data">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
            Import Data
          </h1>
          <p className="text-slate-500 mt-1">
            Import metrics directly from Facebook, Instagram, or TikTok exports
          </p>
        </div>
      </div>

      {/* Content Effect modal */}
      <ContentEffectImporter
        open={contentEffectOpen}
        onClose={() => setContentEffectOpen(false)}
        clients={clients}
        API={API}
        onSaved={() => {}}
      />

      {/* Live Chat modal */}
      <LiveChatImporter
        open={liveChatOpen}
        onClose={() => setLiveChatOpen(false)}
        clients={clients}
        API={API}
        onSaved={() => {}}
      />

      {/* Visitor ID modal */}
      <VisitorIdImporter
        open={visitorIdOpen}
        onClose={() => setVisitorIdOpen(false)}
        clients={clients}
        API={API}
        onSaved={() => {}}
      />

      <PmaxImporter
        open={pmaxOpen}
        onClose={() => setPmaxOpen(false)}
        clients={clients}
        API={API}
        onSaved={() => {}}
      />

      <CampaignSummaryImporter open={csOpen} onClose={() => { setCsOpen(false); refreshHistory(); }} clients={clients} API={API} onImported={() => onImport && onImport()} />
      <DateMismatchDialog check={dateCheck} />

      {/* Best Practices Guide - Collapsible */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl border border-violet-200 overflow-hidden"
      >
        <button
          onClick={() => setShowBestPractices(!showBestPractices)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-violet-100/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Data Import Best Practices Guide</h3>
              <p className="text-sm text-slate-600">Learn the best file formats and data requirements for each platform</p>
            </div>
          </div>
          {showBestPractices ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </button>
        
        <AnimatePresence>
          {showBestPractices && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-violet-200"
            >
              <div className="p-4 space-y-4">
                {/* Quick Reference Table */}
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-900 mb-3">Quick Reference: What to Export</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 pr-4 font-medium text-slate-700">Data Type</th>
                          <th className="text-left py-2 pr-4 font-medium text-slate-700">Best Format</th>
                          <th className="text-left py-2 pr-4 font-medium text-slate-700">Frequency</th>
                          <th className="text-left py-2 font-medium text-slate-700">Key Metrics</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-600">
                        <tr className="border-b border-slate-100">
                          <td className="py-2 pr-4">GA4 Traffic</td>
                          <td className="py-2 pr-4">CSV</td>
                          <td className="py-2 pr-4">Weekly/Monthly</td>
                          <td className="py-2">Sessions, Source, Direct Traffic</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2 pr-4">Meta Paid</td>
                          <td className="py-2 pr-4">PDF/CSV</td>
                          <td className="py-2 pr-4">Monthly</td>
                          <td className="py-2">Impressions, Clicks, Placements, Demographics</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2 pr-4">Mobile/Programmatic</td>
                          <td className="py-2 pr-4">PDF</td>
                          <td className="py-2 pr-4">Monthly</td>
                          <td className="py-2">Impressions, Strategies, Geo-fencing</td>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <td className="py-2 pr-4">CTV/OTT</td>
                          <td className="py-2 pr-4">PDF/CSV</td>
                          <td className="py-2 pr-4">Monthly</td>
                          <td className="py-2">Impressions, Completion Rate (no clicks!)</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-4">Google Ads</td>
                          <td className="py-2 pr-4">CSV</td>
                          <td className="py-2 pr-4">Monthly</td>
                          <td className="py-2">Impressions, Clicks, Conversions, Spend</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Platform-Specific Guidelines */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Google Analytics */}
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded bg-orange-100 flex items-center justify-center">
                        <Search className="w-4 h-4 text-orange-600" />
                      </div>
                      <h4 className="font-semibold text-slate-900">Google Analytics (GA4)</h4>
                    </div>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Export from <strong>Traffic Acquisition</strong> report</li>
                      <li>• Include <strong>Direct</strong> channel for brand recognition</li>
                      <li>• Required: source, medium, sessions, date</li>
                      <li>• Format dates as YYYY-MM-DD</li>
                    </ul>
                  </div>

                  {/* Meta Paid */}
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                        <Facebook className="w-4 h-4 text-blue-600" />
                      </div>
                      <h4 className="font-semibold text-slate-900">Meta Paid Campaigns</h4>
                    </div>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Include <strong>placement breakdown</strong> (Feed, Stories, Reels)</li>
                      <li>• Separate <strong>Prospecting vs Retargeting</strong></li>
                      <li>• Add <strong>demographic data</strong> (gender, age)</li>
                      <li>• Include top performing ad creatives</li>
                    </ul>
                  </div>

                  {/* Mobile/Programmatic */}
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded bg-cyan-100 flex items-center justify-center">
                        <span className="text-sm">📱</span>
                      </div>
                      <h4 className="font-semibold text-slate-900">Mobile Conquesting</h4>
                    </div>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Include all <strong>targeting strategies</strong></li>
                      <li>• List <strong>geo-fencing locations</strong> with addresses</li>
                      <li>• Separate click vs view-through conversions</li>
                      <li>• Include ad creative sizes (300x250, 320x50)</li>
                    </ul>
                  </div>

                  {/* CTV */}
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center">
                        <span className="text-sm">📺</span>
                      </div>
                      <h4 className="font-semibold text-slate-900">CTV / Connected TV</h4>
                    </div>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Focus on <strong>impressions</strong> & <strong>completion rate</strong></li>
                      <li>• Track <strong>publishers</strong> (Hulu, Roku, Pluto)</li>
                      <li className="text-amber-600">⚠️ No clicks/CTR - TV ads aren't clickable!</li>
                    </ul>
                  </div>
                </div>

                {/* File Naming Convention */}
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-2">📁 Recommended File Naming</h4>
                  <p className="text-sm text-amber-800 mb-2">Use consistent naming: <code className="bg-amber-100 px-1 rounded">[ClientName]_[DataType]_[DateRange].pdf</code></p>
                  <div className="text-sm text-amber-700 space-y-1">
                    <p>• <code className="bg-amber-100 px-1 rounded">MackMorris_Meta_March2026.pdf</code></p>
                    <p>• <code className="bg-amber-100 px-1 rounded">RoofTop_MobileConquesting_Q1-2026.pdf</code></p>
                    <p>• <code className="bg-amber-100 px-1 rounded">Sticks_GA4_Jan-Mar2026.csv</code></p>
                  </div>
                </div>

                {/* Tips */}
                <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                  <h4 className="font-semibold text-emerald-900 mb-2">✅ Pro Tips</h4>
                  <ul className="text-sm text-emerald-800 space-y-1">
                    <li>• Keep <strong>date ranges consistent</strong> across all platforms for accurate comparison</li>
                    <li>• <strong>Monthly exports</strong> work best for trend analysis</li>
                    <li>• Always include <strong>Direct traffic</strong> in GA4 exports - it powers brand recognition metrics</li>
                    <li>• Request <strong>placement breakdowns</strong> from Meta for "Where Your Ads Appeared"</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Import Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-slate-100"
        >
          <div className="space-y-6">
            {/* Client Selection - Highlighted as required first step */}
            <div className={`p-4 rounded-xl border-2 transition-colors ${
              selectedClient 
                ? 'border-emerald-200 bg-emerald-50/50' 
                : 'border-amber-300 bg-amber-50 animate-pulse'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                  selectedClient ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-white'
                }`}>
                  1
                </div>
                <Label className={`text-sm font-semibold ${selectedClient ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {selectedClient ? 'Client Selected' : 'First, Select a Client'}
                </Label>
              </div>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger 
                  className={`${selectedClient ? 'border-emerald-300' : 'border-amber-400 bg-white'}`} 
                  data-testid="import-client-select"
                >
                  <SelectValue placeholder="Choose a client to continue..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Manage Locations - for multi-location clients */}
            {isMultiLocation && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-1" data-testid="manage-locations">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-blue-500" />
                  <p className="text-sm font-medium text-blue-800">Locations</p>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {clientLocations.map(loc => (
                    <span key={loc} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {loc}
                      <button
                        onClick={async () => {
                          const updated = clientLocations.filter(l => l !== loc);
                          await axios.put(`${API}/clients/${selectedClient}`, { ...currentClient, locations: updated });
                          onImport();
                          toast.success(`Removed "${loc}"`);
                        }}
                        className="hover:text-red-500 ml-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const input = e.target.elements.newLocation.value.trim();
                  if (!input) return;
                  if (clientLocations.includes(input)) { toast.error("Location already exists"); return; }
                  const updated = [...clientLocations, input];
                  await axios.put(`${API}/clients/${selectedClient}`, { ...currentClient, locations: updated });
                  e.target.elements.newLocation.value = "";
                  onImport();
                  toast.success(`Added "${input}"`);
                }} className="flex gap-2">
                  <input
                    name="newLocation"
                    placeholder="Add location (e.g., Charlottesville)"
                    className="flex-1 border border-blue-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                    data-testid="add-location-input"
                  />
                  <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="add-location-btn">
                    Add
                  </button>
                </form>
              </div>
            )}

            {/* Report Period Banner */}
            {selectedClient && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-3 mb-1" data-testid="import-report-period">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-violet-500 text-lg">📅</span>
                  <p className="text-sm font-medium text-violet-800">Import date range</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-violet-500 block mb-1">Start Date</label>
                    <input
                      type="date"
                      value={importStartDate}
                      onChange={e => setImportStartDate(e.target.value)}
                      className="w-full border border-violet-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-400"
                      data-testid="import-start-date"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-violet-500 block mb-1">End Date</label>
                    <input
                      type="date"
                      value={importEndDate}
                      onChange={e => setImportEndDate(e.target.value)}
                      className="w-full border border-violet-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-400"
                      data-testid="import-end-date"
                    />
                  </div>
                </div>
                {(!importStartDate || !importEndDate) && (
                  <p className="text-xs text-amber-600 mt-2">Set both dates so your data is tagged for the correct period</p>
                )}

                {/* Location selector for multi-location clients */}
                {isMultiLocation && clientLocations.length > 0 && (
                  <div className="mt-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
                    <label className="text-xs text-violet-600 font-semibold block mb-1.5">Assign to Location</label>
                    <select
                      value={selectedLocation}
                      onChange={e => setSelectedLocation(e.target.value)}
                      className="w-full border border-violet-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-400"
                      data-testid="location-select"
                    >
                      <option value="">-- No location (shared/company-wide) --</option>
                      {clientLocations.map(loc => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-violet-400 mt-1">Tag this import with a specific location so it appears under that location's tab</p>
                  </div>
                )}
              </div>
            )}

            {/* Import Checklist + Upload History */}
            {selectedClient && (
              <div className="space-y-3 mb-3">
                {/* Import Checklist */}
                <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="import-checklist">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Import Checklist</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                    {[
                      { type: "Facebook/Instagram", matches: ["Facebook/Meta CSV", "Facebook/Meta PDF"] },
                      { type: "Mobile Conquesting", matches: ["Mobile Conquesting PDF"] },
                      { type: "Google Analytics", matches: ["Google Analytics CSV"] },
                      { type: "CTV", matches: ["CTV / Programmatic", "CTV / Programmatic ZIP", "CTV Dashboard PDF"] },
                      { type: "Social Mirror Ads", matches: ["Social Mirror / Display PDF"] },
                      { type: "Google Ads", matches: ["Google Ads Campaigns"] },
                      { type: "App Front", matches: ["App Front CSV"] },
                    ].map(item => {
                      const hasData = uploadHistory.some(u => item.matches.some(m => u.import_type === m));
                      return (
                        <div key={item.type} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${hasData ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-50 text-slate-400 border border-slate-100'}`} data-testid={`checklist-${item.type}`}>
                          {hasData ? (
                            <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          ) : (
                            <svg className="w-4 h-4 text-slate-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} /></svg>
                          )}
                          {item.type}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Upload History */}
                {uploadHistory.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="upload-history">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-slate-700">Uploaded Files ({uploadHistory.length})</p>
                      <button
                        onClick={handleWipeAllData}
                        className="text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-medium transition-colors border border-red-200"
                        data-testid="wipe-all-data-btn"
                      >
                        Wipe All Data
                      </button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {uploadHistory.map(upload => (
                        <div key={upload.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors" data-testid={`upload-row-${upload.id}`}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${upload.import_type.includes('Facebook') ? 'bg-blue-100 text-blue-600' : upload.import_type.includes('Mobile') ? 'bg-cyan-100 text-cyan-600' : upload.import_type.includes('Analytics') ? 'bg-orange-100 text-orange-600' : upload.import_type.includes('CTV') ? 'bg-purple-100 text-purple-600' : upload.import_type.includes('Mirror') ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-600'}`}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-700 truncate">{upload.filename}</p>
                              <div className="flex items-center gap-2 text-xs text-slate-400">
                                <span className="font-medium text-slate-500">{upload.import_type}</span>
                                <span>|</span>
                                <span>{upload.report_start} — {upload.report_end}</span>
                                <span>|</span>
                                <span>{upload.records_imported} records</span>
                                <span>|</span>
                                <span>{new Date(upload.uploaded_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <button
                              onClick={() => handleReimport(upload)}
                              className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 px-2 py-1.5 rounded-lg hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
                              data-testid={`reimport-upload-${upload.id}`}
                              title="Re-import with same settings"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              <span>Re-import</span>
                            </button>
                            <button
                              onClick={() => handleDeleteUpload(upload.id, upload.filename)}
                              className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
                              data-testid={`delete-upload-${upload.id}`}
                              title="Delete this upload and its data"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {uploadHistory.length === 0 && selectedClient && (
                  <div className="flex justify-end">
                    <button
                      onClick={handleWipeAllData}
                      className="text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-medium transition-colors border border-red-200"
                      data-testid="wipe-all-data-btn-empty"
                    >
                      Wipe All Data
                    </button>
                  </div>
                )}
              </div>
            )}

            {importType === 'connect' ? (
              /* Connect Platforms Mode - API integrations */
              <div className="space-y-4">
                {/* Mode Choice Cards */}
                <div className="grid grid-cols-3 gap-3 mb-2">
            {/* Three modes: Import Data vs Channel Management vs Connect Platforms */}
                  <button
                    onClick={() => setImportType('facebook')}
                    className="flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50 text-left transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Upload className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700 text-sm">Import Data</p>
                      <p className="text-xs text-slate-500">Upload CSV files</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setImportType('channels')}
                    className="flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50 text-left transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Megaphone className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700 text-sm">Channels</p>
                      <p className="text-xs text-slate-500">Manage visibility</p>
                    </div>
                  </button>
                  
                  <button
                    className="flex items-center gap-3 p-4 rounded-xl border-2 border-blue-400 bg-blue-50 text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Link2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-blue-900 text-sm">Connect</p>
                      <p className="text-xs text-blue-600">API integrations</p>
                    </div>
                  </button>
                </div>
                
                {!selectedClient ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg border border-slate-200">
                    <Link2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Please select a client above to manage platform connections</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-blue-900 text-sm mb-1">Connect Ad Platforms</h4>
                      <p className="text-blue-700 text-xs">
                        Connect your ad accounts directly to automatically sync performance data. No more manual CSV exports!
                      </p>
                    </div>
                    
                    {/* Meta Connection */}
                    <MetaConnection 
                      clientId={selectedClient} 
                      onConnectionChange={(connected) => {
                        if (connected) {
                          toast.success("Meta connection updated");
                        }
                      }}
                    />
                    
                    {/* Coming Soon: TikTok */}
                    <div className="bg-slate-100 rounded-xl p-6 border border-slate-200 opacity-60">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center">
                          <TikTokIcon />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-500">TikTok Ads</h3>
                          <p className="text-slate-400 text-sm">Coming soon</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Coming Soon: Google */}
                    <div className="bg-slate-100 rounded-xl p-6 border border-slate-200 opacity-60">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center">
                          <GoogleIcon />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-500">Google Ads</h3>
                          <p className="text-slate-400 text-sm">Coming soon</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : importType === 'channels' ? (
              /* Channel Management Mode - cleaner interface */
              <div className="space-y-4">
                {/* Mode Choice Cards */}
                <div className="grid grid-cols-3 gap-3 mb-2">
                  <button
                    onClick={() => setImportType('facebook')}
                    className="flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50 text-left transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Upload className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700 text-sm">Import Data</p>
                      <p className="text-xs text-slate-500">Upload CSV files</p>
                    </div>
                  </button>
                  
                  <button
                    className="flex items-center gap-3 p-4 rounded-xl border-2 border-emerald-400 bg-emerald-50 text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Megaphone className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-emerald-900 text-sm">Channels</p>
                      <p className="text-xs text-emerald-600">Manage visibility</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setImportType('connect')}
                    className="flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50 text-left transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Link2 className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700 text-sm">Connect</p>
                      <p className="text-xs text-slate-500">API integrations</p>
                    </div>
                  </button>
                </div>
                
                {!selectedClient ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg border border-slate-200">
                    <Megaphone className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Please select a client above to manage their channels</p>
                  </div>
                ) : loadingChannels ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <p className="text-slate-500">Loading channels...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Summary of enabled channels */}
                    {marketingChannels.filter(c => c.is_active).length > 0 && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Enabled Channels ({marketingChannels.filter(c => c.is_active).length})
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {marketingChannels.filter(c => c.is_active).map(channel => {
                            const info = availableChannels.find(a => a.id === channel.channel_type);
                            return (
                              <span key={channel.id} className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium">
                                <span>{info?.icon}</span>
                                {info?.name || channel.channel_type}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Channel toggles by category */}
                    {['Paid Social', 'Video', 'Audio', 'Social', 'Direct', 'Loyalty', 'Traditional'].map(category => (
                      <div key={category}>
                        <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{category}</h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {availableChannels.filter(c => c.category === category).map(channel => {
                            const isActive = isChannelActive(channel.id);
                            return (
                              <button
                                key={channel.id}
                                onClick={() => toggleChannel(channel.id)}
                                className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                                  isActive
                                    ? 'bg-emerald-50 border-emerald-400 shadow-sm'
                                    : 'bg-slate-50 border-slate-200 hover:border-slate-300 opacity-60'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <span className="text-xl">{channel.icon}</span>
                                  <span className={`font-medium text-sm ${isActive ? 'text-emerald-900' : 'text-slate-500'}`}>
                                    {channel.name}
                                  </span>
                                </div>
                                {/* Toggle indicator */}
                                <div className={`w-12 h-6 rounded-full p-1 transition-colors ${
                                  isActive ? 'bg-emerald-500' : 'bg-slate-300'
                                }`}>
                                  <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                                    isActive ? 'translate-x-6' : 'translate-x-0'
                                  }`} />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    
                    <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-700">
                        <strong>Tip:</strong> Only enabled channels will appear in the "Your Channels" section of the Brand Exposure Story. Toggle channels ON to include them in client reports.
                      </p>
                    </div>

                    {/* Brand Channels - Offline/Broadcast */}
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <h4 className="text-sm font-bold text-slate-800 mb-1 flex items-center gap-2">
                        <Tv className="w-4 h-4 text-violet-600" />
                        Active Brand Channels
                      </h4>
                      <p className="text-xs text-slate-500 mb-4">Add offline/broadcast channels like "Local TV CBS19" or "Local Radio WNRN" — these will appear on the client report.</p>

                      {/* Existing brand channels */}
                      {brandChannels.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {brandChannels.map((ch, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-slate-200 shadow-sm">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                                  {ch.name?.toLowerCase().includes('tv') ? <Tv className="w-4 h-4 text-violet-600" /> :
                                   ch.name?.toLowerCase().includes('radio') ? <Radio className="w-4 h-4 text-violet-600" /> :
                                   <Megaphone className="w-4 h-4 text-violet-600" />}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-800 truncate">{ch.name}</p>
                                  <div className="flex gap-3 text-xs text-slate-500">
                                    {ch.impressions && <span>{parseInt(ch.impressions).toLocaleString()} impressions</span>}
                                    {ch.local_spots && <span>{parseInt(ch.local_spots).toLocaleString()} local spots</span>}
                                    {ch.report_start && <span>{ch.report_start} to {ch.report_end}</span>}
                                    {ch.description && <span className="text-slate-400 italic truncate max-w-[200px]">{ch.description}</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 ml-2">
                                <button
                                  onClick={() => {
                                    setNewBrandChannel({
                                      name: ch.name || "",
                                      impressions: ch.impressions || "",
                                      local_spots: ch.local_spots || "",
                                      report_start: ch.report_start || "",
                                      report_end: ch.report_end || "",
                                      description: ch.description || "",
                                      notes: ch.notes || "",
                                      editIdx: idx,
                                    });
                                  }}
                                  className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                                  data-testid={`edit-brand-channel-${idx}`}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => removeBrandChannel(idx)}
                                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  data-testid={`remove-brand-channel-${idx}`}
                                  title={`Remove ${ch.name}`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Quick presets */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {[
                          { label: "Local TV", icon: Tv },
                          { label: "Local Radio", icon: Radio },
                          { label: "Billboard / OOH", icon: Megaphone },
                          { label: "Print / Mail", icon: Mail },
                        ].map(preset => {
                          const PresetIcon = preset.icon;
                          return (
                            <button
                              key={preset.label}
                              onClick={() => setNewBrandChannel(prev => ({ ...prev, name: prev.name || preset.label }))}
                              className="flex items-center gap-1.5 text-xs bg-violet-50 text-violet-700 px-3 py-1.5 rounded-lg hover:bg-violet-100 transition-colors border border-violet-200"
                            >
                              <PresetIcon className="w-3.5 h-3.5" />
                              {preset.label}
                            </button>
                          );
                        })}
                      </div>

                      {/* Add/Edit channel form */}
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-3">
                        <div className="flex gap-2">
                          <input
                            value={newBrandChannel.name}
                            onChange={(e) => setNewBrandChannel(prev => ({ ...prev, name: e.target.value }))}
                            placeholder='Channel name (e.g., "Local TV CBS19")'
                            className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-violet-400 focus:ring-1 focus:ring-violet-400 outline-none"
                            data-testid="new-brand-channel-input"
                            onKeyDown={(e) => { if (e.key === 'Enter') addBrandChannel(); }}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Impressions</label>
                            <input
                              type="number"
                              value={newBrandChannel.impressions}
                              onChange={(e) => setNewBrandChannel(prev => ({ ...prev, impressions: e.target.value }))}
                              placeholder="e.g., 50000"
                              className="w-full px-3 py-1.5 text-sm rounded-lg border border-slate-300 outline-none focus:border-violet-400"
                              data-testid="brand-channel-impressions"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Local Spots</label>
                            <input
                              type="number"
                              value={newBrandChannel.local_spots || ""}
                              onChange={(e) => setNewBrandChannel(prev => ({ ...prev, local_spots: e.target.value }))}
                              placeholder="e.g., 120"
                              className="w-full px-3 py-1.5 text-sm rounded-lg border border-slate-300 outline-none focus:border-violet-400"
                              data-testid="brand-channel-spots"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Start Date</label>
                            <input
                              type="date"
                              value={newBrandChannel.report_start}
                              onChange={(e) => setNewBrandChannel(prev => ({ ...prev, report_start: e.target.value }))}
                              className="w-full px-3 py-1.5 text-sm rounded-lg border border-slate-300 outline-none focus:border-violet-400"
                              data-testid="brand-channel-start"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">End Date</label>
                            <input
                              type="date"
                              value={newBrandChannel.report_end}
                              onChange={(e) => setNewBrandChannel(prev => ({ ...prev, report_end: e.target.value }))}
                              className="w-full px-3 py-1.5 text-sm rounded-lg border border-slate-300 outline-none focus:border-violet-400"
                              data-testid="brand-channel-end"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Description (optional)</label>
                          <input
                            value={newBrandChannel.description || ""}
                            onChange={(e) => setNewBrandChannel(prev => ({ ...prev, description: e.target.value }))}
                            placeholder='e.g., "30-sec spots on CBS19 morning news"'
                            className="w-full px-3 py-1.5 text-sm rounded-lg border border-slate-300 outline-none focus:border-violet-400"
                            data-testid="brand-channel-description"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Notes (optional)</label>
                          <input
                            value={newBrandChannel.notes || ""}
                            onChange={(e) => setNewBrandChannel(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Internal notes"
                            className="w-full px-3 py-1.5 text-sm rounded-lg border border-slate-300 outline-none focus:border-violet-400"
                            data-testid="brand-channel-notes"
                          />
                        </div>
                        <div className="flex gap-2 pt-1">
                          <Button
                            onClick={addBrandChannel}
                            disabled={!newBrandChannel.name.trim()}
                            className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg px-6"
                            data-testid="add-brand-channel-btn"
                          >
                            {newBrandChannel.editIdx !== undefined ? "Update Channel" : "Add Channel"}
                          </Button>
                          {newBrandChannel.name && (
                            <Button
                              variant="outline"
                              onClick={() => setNewBrandChannel({ name: "", impressions: "", local_spots: "", report_start: "", report_end: "", description: "", notes: "" })}
                              className="rounded-lg"
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Target Zip Codes for Geofencing */}
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <h4 className="text-sm font-bold text-slate-800 mb-1 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-violet-600" />
                        Target Zip Codes
                      </h4>
                      <p className="text-xs text-slate-500 mb-3">Enter zip codes targeted by geofencing campaigns (Social Mirror, Mobile Conquesting). These will appear as shaded areas on the "Where Your Ads Run" map.</p>
                      <textarea
                        value={zipcodeInput}
                        onChange={(e) => setZipcodeInput(e.target.value)}
                        placeholder="Enter zip codes separated by commas, e.g.: 23129, 23113, 20129, 22066"
                        className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-300 focus:border-violet-400 focus:ring-1 focus:ring-violet-400 outline-none resize-none h-20 font-mono"
                        data-testid="target-zipcodes-input"
                      />
                      <div className="flex items-center gap-3 mt-2">
                        <Button
                          onClick={saveTargetZipcodes}
                          disabled={savingZipcodes}
                          className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg px-6"
                          data-testid="save-zipcodes-btn"
                        >
                          {savingZipcodes ? "Saving..." : "Save Zip Codes"}
                        </Button>
                        <span className="text-xs text-slate-400">
                          {zipcodeInput.split(/[,\s\n]+/).filter(z => /^\d{5}$/.test(z.trim())).length} valid zip codes detected
                        </span>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            ) : (
              /* Import Data Mode - with tab bar */
              <>
                {/* Mode Choice Cards */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <button
                    className="flex items-center gap-3 p-4 rounded-xl border-2 border-violet-400 bg-violet-50 text-left"
                  >
                    <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                      <Upload className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-violet-900 text-sm">Import Data</p>
                      <p className="text-xs text-violet-600">Upload CSV files</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setImportType('channels')}
                    className="flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50 text-left transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Megaphone className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700 text-sm">Channels</p>
                      <p className="text-xs text-slate-500">Manage visibility</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setImportType('connect')}
                    className="flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50 text-left transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Link2 className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700 text-sm">Connect</p>
                      <p className="text-xs text-slate-500">API integrations</p>
                    </div>
                  </button>
                </div>
                
                {/* Special Report Imports — modal-based flows that don't
                    share the standard CSV/PDF upload form. Placed here so
                    agencies see them alongside the platform tabs instead of
                    tucked in the page header. */}
                <div className="mb-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Special Report Imports</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    <button
                      onClick={() => setContentEffectOpen(true)}
                      className="flex items-center gap-3 p-4 rounded-xl border-2 border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-indigo-50 hover:border-fuchsia-400 hover:shadow-md text-left transition-all"
                      data-testid="open-content-effect-importer"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-fuchsia-500 to-indigo-500 flex items-center justify-center flex-shrink-0 text-white text-lg">
                        ✨
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm">Add Content Effect</p>
                        <p className="text-xs text-slate-600">Screenshot import from Meta Business Suite</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setLiveChatOpen(true)}
                      className="flex items-center gap-3 p-4 rounded-xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 hover:border-cyan-400 hover:shadow-md text-left transition-all"
                      data-testid="open-live-chat-importer"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0 text-white text-lg">
                        💬
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm">Add Live Chat</p>
                        <p className="text-xs text-slate-600">PDF import from CallCenter Live Chat</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setVisitorIdOpen(true)}
                      className="flex items-center gap-3 p-4 rounded-xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50 hover:border-violet-400 hover:shadow-md text-left transition-all"
                      data-testid="open-visitor-id-importer"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0 text-white text-lg">
                        👥
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm">Add Visitor ID</p>
                        <p className="text-xs text-slate-600">PDF import from Website Visitor ID vendor</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setPmaxOpen(true)}
                      className="flex items-center gap-3 p-4 rounded-xl border-2 border-lime-200 bg-gradient-to-br from-lime-50 to-cyan-50 hover:border-lime-400 hover:shadow-md text-left transition-all"
                      data-testid="open-pmax-importer"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-lime-500 to-cyan-500 flex items-center justify-center flex-shrink-0 text-white">
                        <PmaxIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm">Add PMAX</p>
                        <p className="text-xs text-slate-600">Performance Max PDF, single-location clients</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setCsOpen(true)}
                      className="flex items-center gap-3 p-4 rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-sky-50 hover:border-emerald-400 hover:shadow-md text-left transition-all"
                      data-testid="open-campaign-summary-importer"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-sky-500 flex items-center justify-center flex-shrink-0 text-white">
                        <Layers className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm">Add Campaign Summary</p>
                        <p className="text-xs text-slate-600">Any summary report, one or many line items</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Platform Tabs */}
                <Tabs value={importType} onValueChange={setImportType} className="w-full">
                  <TabsList className="grid w-full grid-cols-8 bg-slate-100 p-1 rounded-xl">
                    <TabsTrigger
                      value="facebook"
                      className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1 text-xs"
                      data-testid="tab-facebook"
                    >
                      <Facebook className="w-4 h-4 text-blue-600" />
                      FB/IG
                    </TabsTrigger>
                    <TabsTrigger
                      value="tiktok"
                      className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1 text-xs"
                      data-testid="tab-tiktok"
                    >
                      <TikTokIcon />
                      TikTok
                    </TabsTrigger>
                    <TabsTrigger
                      value="google"
                      className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1 text-xs"
                      data-testid="tab-google"
                    >
                      <GoogleIcon />
                      Google Ads
                    </TabsTrigger>
                    <TabsTrigger
                      value="analytics"
                      className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1 text-xs"
                      data-testid="tab-analytics"
                    >
                      <Search className="w-4 h-4 text-orange-500" />
                      Google Analytics
                    </TabsTrigger>
                    <TabsTrigger
                      value="campaign-dashboard"
                      className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs"
                      data-testid="tab-campaign-dashboard"
                    >
                      <Tv className="w-4 h-4 inline mr-1" /> CTV
                    </TabsTrigger>
                    <TabsTrigger
                      value="social-mirror"
                      className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs"
                      data-testid="tab-social-mirror"
                    >
                      <Newspaper className="w-4 h-4 inline mr-1" /> Social Mirror Ads
                    </TabsTrigger>
                    <TabsTrigger
                      value="mobile-conquesting"
                      className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs"
                      data-testid="tab-mobile-conquesting"
                    >
                      <Globe className="w-4 h-4 inline mr-1" /> Mobile
                    </TabsTrigger>
                    <TabsTrigger
                      value="appfront"
                      className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs"
                      data-testid="tab-appfront"
                    >
                      <Mail className="w-4 h-4 inline mr-1" /> App Front
                    </TabsTrigger>
                  </TabsList>
                  {dupExisting?.exists && (
                    <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-sm" data-testid="dup-existing-warning">
                      Heads up: {dupExisting.count} upload{dupExisting.count > 1 ? "s" : ""} of this type already cover these dates
                      {dupExisting.uploads?.[0] ? <> (latest: <b>{dupExisting.uploads[0].filename}</b>, {dupExisting.uploads[0].report_start} to {dupExisting.uploads[0].report_end})</> : null}.
                      Uploading the same report again will count its numbers twice. If you're replacing it, delete the old one in Upload History first.
                    </div>
                  )}


              <TabsContent value="facebook" className="mt-4">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-900 flex items-center gap-2">
                    <Facebook className="w-5 h-5" />
                    Facebook / Instagram Ads Import
                  </h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Import your Facebook/Instagram (Meta) ads data from <strong>CSV</strong> (Ads Manager export) or <strong>PDF</strong> (campaign reports). We'll extract impressions, clicks, spend, and performance metrics.
                  </p>
                  <div className="mt-3 text-xs text-blue-600">
                    <strong>Supported formats:</strong> CSV from Ads Manager, PDF campaign reports
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="tiktok" className="mt-4">
                <div className="bg-slate-100 border border-slate-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-slate-900 flex items-center gap-2">
                    <TikTokIcon />
                    TikTok Ads Manager Export
                  </h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Export your campaign report or audience demographics from TikTok Ads Manager. We'll auto-detect the format and map columns automatically.
                  </p>
                  <div className="mt-3 text-xs text-slate-500">
                    <strong>Supported formats:</strong> Campaign/Ad performance CSVs, Audience Report (Age demographics)
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="google" className="mt-4">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-900 flex items-center gap-2">
                    <GoogleIcon />
                    Google Ads Export
                  </h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Export Time Series data from Google Ads. We'll auto-map columns like "Date", "Clicks", "Impressions", "Avg. CPC", and "Cost".
                  </p>
                  <p className="text-xs text-blue-600 mt-2" data-testid="google-search-terms-hint">
                    <strong>Search terms:</strong> also upload the "Search terms" report CSV (Insights and reports, Search terms, Download) to show what people typed to find the client.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="analytics" className="mt-4">
                <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-orange-900 flex items-center gap-2">
                    <Search className="w-5 h-5" />
                    Google Analytics (GA4) Export
                  </h4>
                  <p className="text-sm text-orange-700 mt-1">
                    Export traffic data from GA4 or Universal Analytics. Include: Source/Medium, Sessions, Bounce Rate, Conversions.
                  </p>
                  <div className="mt-3 text-xs text-orange-600">
                    <strong>Recommended columns:</strong> Source, Medium, Sessions, Users, Bounce Rate, Avg. Session Duration, Conversions
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="campaign-dashboard" className="mt-4">
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-purple-900 flex items-center gap-2">
                    CTV / Programmatic Campaign Import
                  </h4>
                  <p className="text-sm text-purple-700 mt-1">
                    Import your programmatic ad platform data. Upload a <strong>PDF</strong> (Campaign Summary Dashboard), a <strong>ZIP file</strong>, or individual CSVs. We'll automatically extract campaign metrics, line items, creatives, device breakdown, audience targeting, CTV publishers, and conversions.
                  </p>
                  <div className="mt-3 text-xs text-purple-600">
                    <strong>PDF (recommended):</strong> Upload the Campaign Summary Dashboard PDF — we'll AI-extract everything including conversions and publishers.
                    <br /><strong>ZIP:</strong> Upload the full CSV download from your platform.
                    <br /><strong>Standalone CSVs:</strong> Upload individual files like "Top CTV Publishers" directly.
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="social-mirror" className="mt-4">
                <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-cyan-900 flex items-center gap-2">
                    Social Mirror Ads Import
                  </h4>
                  <p className="text-sm text-cyan-700 mt-1">
                    Import PDF reports from your Social Mirror or Display ad campaigns. We'll extract impressions, clicks, CTR, conversions, device breakdown, and creative performance.
                  </p>
                  <div className="mt-3 text-xs text-cyan-600">
                    <strong>Supported data:</strong> Campaign metrics, Line item performance, Click & View-through conversions, Device breakdown (Mobile/Desktop/Tablet), Creative performance
                  </div>
                  <p className="mt-2 text-xs text-cyan-800"><strong>One report per month:</strong> uploading another Social Mirror report for the same client and month replaces the earlier one, so numbers never double.</p>
                  <button
                    type="button"
                    onClick={() => setCsOpen(true)}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-cyan-300 text-cyan-800 text-xs font-semibold hover:bg-cyan-100 transition-colors"
                    data-testid="open-sm-summary-importer"
                  >
                    + Add Campaign Summary (adds the day-by-day chart)
                  </button>
                </div>
                {smExisting?.exists && (
                  <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-sm" data-testid="sm-existing-warning">
                    {smExisting.exact ? (
                      <>A Social Mirror report for this client and period is already loaded ({smExisting.impressions.toLocaleString()} impressions, {smExisting.clicks.toLocaleString()} clicks). Uploading another will <b>replace</b> it, not add to it.</>
                    ) : (
                      <>{smExisting.count} Social Mirror report{smExisting.count > 1 ? "s" : ""} already cover part of these dates ({smExisting.impressions.toLocaleString()} impressions). Uploading a report for this wider range would <b>add on top</b> and double count. Set the dates to one month that isn't loaded yet.</>
                    )}{" "}For the Campaign Summary PDF, use "Add Campaign Summary" above instead.
                  </div>
                )}
              </TabsContent>

              <TabsContent value="mobile-conquesting" className="mt-4">
                <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-orange-900 flex items-center gap-2">
                    📱 Mobile Conquesting / Geofencing
                  </h4>
                  <p className="text-sm text-orange-700 mt-1">
                    Import PDF reports from your Mobile Conquesting campaigns. We'll extract impressions, clicks, geofencing locations, targeting strategies, and conversions.
                  </p>
                  <div className="mt-3 text-xs text-orange-600">
                    <strong>Supported data:</strong> Campaign metrics, Line items (strategies), Geofencing locations with addresses, View-through conversions, Creative performance with national avg comparison
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="appfront" className="mt-4">
                <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-green-900 flex items-center gap-2">
                    📲 App Front — Push, Email & SMS Campaigns
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    Import CSV reports from App Front. We'll extract campaign performance including email open rates, push notification delivery, coupon redemptions, and <strong>revenue attribution</strong>.
                  </p>
                  <div className="mt-3 text-xs text-green-600">
                    <strong>Supported data:</strong> Email (delivered, opened, clicked), Push (delivered, seen), SMS, Coupon redemptions, Total generated sales, Avg purchase value
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Drop Zone */}
            <div
              ref={uploadAreaRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                isDragging
                  ? "border-violet-500 bg-violet-50"
                  : file
                  ? "border-emerald-500 bg-emerald-50"
                  : "border-slate-200 hover:border-violet-300"
              }`}
              data-testid="drop-zone"
            >
              {file ? (
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                    <FileText className="w-8 h-8 text-emerald-600" />
                  </div>
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <Button
                    variant="ghost"
                    onClick={() => setFile(null)}
                    className="mt-4 text-slate-500"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="font-medium text-slate-900 mb-1">
                    Drop your {importType === 'campaign-dashboard' ? 'PDF, ZIP, or CSV' : ['social-mirror', 'facebook', 'instagram', 'mobile-conquesting'].includes(importType) ? 'PDF or CSV' : 'CSV'} file here
                  </p>
                  <p className="text-sm text-slate-500 mb-4">or click to browse</p>
                  <label>
                    <input
                      type="file"
                      accept={importType === 'campaign-dashboard' ? '.pdf,.zip,.csv,.xlsx,.xls' : ['social-mirror', 'facebook', 'instagram', 'mobile-conquesting'].includes(importType) ? '.pdf,.csv' : '.csv,.xlsx,.xls'}
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full"
                      asChild
                    >
                      <span>Browse Files</span>
                    </Button>
                  </label>
                </div>
              )}
            </div>

            {/* Upload Result */}
            {uploadResult && (
              <div
                className={`flex items-start gap-3 p-4 rounded-lg ${
                  uploadResult.success
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-red-50 text-red-700"
                }`}
              >
                {uploadResult.success ? (
                  <CheckCircle className="w-5 h-5 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 mt-0.5" />
                )}
                <div>
                  <p className="font-medium">{uploadResult.message}</p>
                  {uploadResult.success && uploadResult.details && (
                    <div className="text-sm mt-2 space-y-1">
                      {uploadResult.details.ads_imported !== undefined && (
                        <>
                          <p>• {uploadResult.details.ads_imported} individual ads imported</p>
                          <p>• {uploadResult.details.campaigns_imported} campaign summaries created</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end items-center gap-3">
              {!selectedClient && file && (
                <span className="text-amber-600 text-sm font-medium">← Please select a client first</span>
              )}
              <Button
                onClick={handleUpload}
                disabled={!file || !selectedClient || isUploading}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 shadow-lg shadow-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {isUploading ? "Importing..." : !selectedClient ? "Select Client First" : "Import Data"}
              </Button>
            </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Help Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          {/* Export Instructions */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-4">
              How to Export
            </h3>
            
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-blue-50">
                <h4 className="font-medium text-blue-900 text-sm flex items-center gap-2">
                  <Facebook className="w-4 h-4" /> Facebook
                </h4>
                <ol className="text-xs text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Go to Ads Manager → Reports</li>
                  <li>Select date range and breakdown</li>
                  <li>Click Export → CSV</li>
                </ol>
              </div>
              
              <div className="p-3 rounded-lg bg-slate-100">
                <h4 className="font-medium text-slate-900 text-sm flex items-center gap-2">
                  <TikTokIcon /> TikTok
                </h4>
                <ol className="text-xs text-slate-600 mt-2 space-y-1 list-decimal list-inside">
                  <li>Go to TikTok Ads Manager</li>
                  <li>Select Campaigns → Export</li>
                  <li>Choose CSV format</li>
                </ol>
              </div>

              <div className="p-3 rounded-lg bg-green-50">
                <h4 className="font-medium text-green-900 text-sm flex items-center gap-2">
                  <GoogleIcon /> Google Ads
                </h4>
                <ol className="text-xs text-green-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Go to Google Ads → Reports</li>
                  <li>Select Time Series report</li>
                  <li>Export as CSV</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Templates */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-4">
              CSV Templates
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              For custom imports, download these templates.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => downloadTemplate("metrics")}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                data-testid="download-metrics-template"
              >
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Download className="w-4 h-4 text-violet-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Platform Metrics</p>
                  <p className="text-xs text-slate-500">Impressions, clicks, spend</p>
                </div>
              </button>

              <button
                onClick={() => downloadTemplate("ads")}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                data-testid="download-ads-template"
              >
                <div className="w-8 h-8 rounded-lg bg-pink-100 flex items-center justify-center">
                  <Download className="w-4 h-4 text-pink-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Ad Metrics</p>
                  <p className="text-xs text-slate-500">Ad creative performance</p>
                </div>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
